import type { SupabaseClient } from '../client'
import type { KnowledgeArticle, KnowledgeBase } from '../types/conhecimento'
import { unwrapList, unwrapMaybe, unwrapSingle } from './_helpers'

const BASE_COLUMNS =
  'id, slug, name, type, entity_initiative_id, owner_usuario_id, is_active, created_at, updated_at'

const ARTICLE_LIST_COLUMNS =
  'id, base_id, slug, title, summary, status, visibility, module, subarea, category, sort_order, created_at, updated_at, published_at'

const ARTICLE_FULL_COLUMNS =
  'id, base_id, slug, title, summary, content, status, visibility, module, subarea, category, sort_order, related_route, created_at, updated_at, published_at'

/**
 * Resolve a base de conhecimento de uma iniciativa.
 * Retorna `null` se a iniciativa nao tem base criada ainda.
 */
export async function getInitiativeKnowledgeBase(
  client: SupabaseClient,
  initiativeId: number,
): Promise<KnowledgeBase | null> {
  // biome-ignore lint/suspicious/noExplicitAny: schema conhecimento ainda nao esta nos tipos gerados
  const result = await (client as any)
    .schema('conhecimento')
    .from('bases')
    .select(BASE_COLUMNS)
    .eq('type', 'iniciativa')
    .eq('entity_initiative_id', initiativeId)
    .eq('is_active', true)
    .maybeSingle()
  return unwrapMaybe(result) as KnowledgeBase | null
}

export type ListArticlesOptions = {
  category?: string | null
  search?: string | null
}

/**
 * Lista artigos publicados e com visibility=client da base. Ordenados por
 * sort_order asc + updated_at desc (fallback).
 */
export async function listPublishedClientArticles(
  client: SupabaseClient,
  baseId: string | number,
  options: ListArticlesOptions = {},
): Promise<Omit<KnowledgeArticle, 'content'>[]> {
  // biome-ignore lint/suspicious/noExplicitAny: schema conhecimento ainda nao esta nos tipos gerados
  let query = (client as any)
    .schema('conhecimento')
    .from('articles')
    .select(ARTICLE_LIST_COLUMNS)
    .eq('base_id', baseId)
    .eq('status', 'published')
    .eq('visibility', 'client')

  if (options.category) {
    query = query.eq('category', options.category)
  }
  if (options.search?.trim()) {
    const term = `%${options.search.trim()}%`
    query = query.or(`title.ilike.${term},summary.ilike.${term}`)
  }
  query = query
    .order('sort_order', { ascending: true, nullsFirst: false })
    .order('updated_at', { ascending: false })

  return unwrapList(await query) as Omit<KnowledgeArticle, 'content'>[]
}

/**
 * Carrega um artigo completo (com content). RLS bloqueia se nao for
 * published+client ou se a base nao for acessivel.
 */
export async function getArticleById(
  client: SupabaseClient,
  articleId: number,
): Promise<KnowledgeArticle> {
  // biome-ignore lint/suspicious/noExplicitAny: schema conhecimento ainda nao esta nos tipos gerados
  const result = await (client as any)
    .schema('conhecimento')
    .from('articles')
    .select(ARTICLE_FULL_COLUMNS)
    .eq('id', articleId)
    .single()
  return unwrapSingle(result, 'Article', articleId) as KnowledgeArticle
}
